import MenuBookIcon from "@mui/icons-material/MenuBook";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import Typography from "@mui/material/Typography";
import { CustomLink } from "core/components/CustomLink";
import { ExpandButton } from "core/components/MobileNav/ExpandButton";
import getConfig from "next/config";
import { memo, useState } from "react";
import { useTranslation } from "react-i18next";

const {
  COMMUNITY_ISSUE_HREF,
  COMMUNITY_ISSUE_TARGET = "_self",
  COMMUNITY_RELEASE_NOTES_HREF,
  COMMUNITY_RELEASE_NOTES_TARGET = "_self",
} = getConfig()?.publicRuntimeConfig ?? {};

export const CommunityFeedback = memo(() => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:CommunityFeedback",
  });
  function closeMenu() {
    setAnchorEl(null);
  }

  if (!COMMUNITY_ISSUE_HREF && !COMMUNITY_RELEASE_NOTES_HREF) {
    return null;
  }

  return (
    <>
      <IconButton
        data-testid="community-improvements-menu-button"
        aria-label={t("buttonAriaLabel")}
        sx={{ color: "primary.contrastText" }}
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
        }}
      >
        <MenuBookIcon color="inherit" />
      </IconButton>
      <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={closeMenu}>
        <CommunityFeedbackContent />
      </Menu>
    </>
  );
});
CommunityFeedback.displayName = "CommunityFeedback";

export function CommunityFeedbackContent() {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:CommunityFeedback",
  });
  return (
    <>
      {COMMUNITY_ISSUE_HREF && (
        <>
          <Divider textAlign="center">
            <Typography color="text.secondary" variant="overline">
              {t("communityFeedback")}
            </Typography>
          </Divider>
          <ListItemButton
            href={COMMUNITY_ISSUE_HREF}
            target={COMMUNITY_ISSUE_TARGET}
            component={COMMUNITY_ISSUE_TARGET === "_blank" ? "a" : CustomLink}
          >
            <ListItemText primary={t("submitIssue")} />
          </ListItemButton>
        </>
      )}
      {COMMUNITY_RELEASE_NOTES_HREF && (
        <>
          <Divider textAlign="center">
            <Typography color="text.secondary" variant="overline">
              {t("whatIsNew")}
            </Typography>
          </Divider>
          <ListItemButton
            href={COMMUNITY_RELEASE_NOTES_HREF}
            target={COMMUNITY_RELEASE_NOTES_TARGET}
            component={
              COMMUNITY_RELEASE_NOTES_TARGET === "_blank" ? "a" : CustomLink
            }
          >
            <ListItemText primary={t("releaseNotes")} />
          </ListItemButton>
        </>
      )}
    </>
  );
}

export function MobileCommunityFeedback() {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:CommunityFeedback",
  });

  if (!COMMUNITY_ISSUE_HREF && !COMMUNITY_RELEASE_NOTES_HREF) {
    return null;
  }

  return (
    <>
      <ListItem
        secondaryAction={
          <ExpandButton
            open={open}
            onClick={() => {
              setOpen((currentOpenState) => !currentOpenState);
            }}
          />
        }
      >
        <ListItemText
          primary={t("communityFeedback")}
          slotProps={{ primary: { fontWeight: "bold" } }}
        />
      </ListItem>
      <Collapse in={open}>
        <List dense>
          {COMMUNITY_ISSUE_HREF && (
            <ListItemButton
              href={COMMUNITY_ISSUE_HREF}
              target={COMMUNITY_ISSUE_TARGET}
              component={COMMUNITY_ISSUE_TARGET === "_blank" ? "a" : CustomLink}
            >
              <ListItemText primary={t("submitIssue")} />
            </ListItemButton>
          )}
          {COMMUNITY_RELEASE_NOTES_HREF && (
            <ListItemButton
              href={COMMUNITY_RELEASE_NOTES_HREF}
              target={COMMUNITY_RELEASE_NOTES_TARGET}
              component={
                COMMUNITY_RELEASE_NOTES_TARGET === "_blank" ? "a" : CustomLink
              }
            >
              <ListItemText primary={t("releaseNotes")} />
            </ListItemButton>
          )}
        </List>
      </Collapse>
      <Divider sx={{ my: 2 }} />
    </>
  );
}
